import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Flask, Envelope, Lock, User, WarningCircle, Check } from '@phosphor-icons/react';
import api from '../../services/api';

const LoginPage = () => {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgot, setShowForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'researcher'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPendingApproval(false);
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(formData.email, formData.password, formData.name, formData.role);
      }
      
      if (result.pending) {
        setPendingApproval(true);
      } else if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setLoading(true);
    setError('');
    try {
      await api.request('POST', '/auth/forgot-password', { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="auth-container">
      {/* Floating ambient orbs */}
      <div className="absolute top-[10%] left-[15%] w-[400px] h-[400px] rounded-full opacity-30 pointer-events-none" 
           style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      <div className="absolute bottom-[10%] right-[10%] w-[350px] h-[350px] rounded-full opacity-25 pointer-events-none" 
           style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      {/* Main content - two column on large screens */}
      <div className="relative z-10 flex flex-col lg:flex-row items-start gap-8 lg:gap-12 px-6 lg:px-16 max-w-[1400px] mx-auto w-full">
        
        {/* Left side - Lab branding (hidden on small screens) */}
        <div className="hidden lg:flex flex-col items-start flex-[1.4] max-w-2xl">
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-2" 
              style={{ fontFamily: '"Space Grotesk", sans-serif', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
            P.R.I.S.M.
          </h1>
          <p className="text-sm font-semibold tracking-widest uppercase text-slate-400 mb-3">
            Programmable Research Integration & Systems Management
          </p>
          <p className="text-base font-semibold mb-4"
             style={{ 
               fontFamily: '"Space Grotesk", sans-serif',
               background: 'linear-gradient(135deg, rgb(139,92,246) 0%, rgb(6,182,212) 50%, rgb(59,130,246) 100%)',
               WebkitBackgroundClip: 'text',
               WebkitTextFillColor: 'transparent',
               lineHeight: 1.4
             }}>
            A Unified Framework for Biological Orchestration
          </p>
          <div className="pt-3 border-t border-white/[0.06] w-full">
            <p className="text-sm text-slate-400 leading-relaxed mb-4">
              The Chaturvedi Lab developed P.R.I.S.M. as an internal response to the systemic friction inherent in modern discovery. By integrating the entire research lifecycle into a single digital thread, we have eliminated the fragmented workflows that traditionally stall synthetic biology.
            </p>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2.5">The Architecture of Discovery</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px] text-slate-400 leading-relaxed">
              <div className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-indigo-400 flex-shrink-0" />
                <span><span className="text-slate-300 font-medium">Translational Continuity</span> — Paper-to-Protocol engine collapses the timeline of methodology development, converting literature into validated bench workflows.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-cyan-400 flex-shrink-0" />
                <span><span className="text-slate-300 font-medium">Precision Engineering</span> — CRISPR design, sequence management, and real-time lineage graphing with absolute data provenance from construct to phenotypic readout.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-indigo-400 flex-shrink-0" />
                <span><span className="text-slate-300 font-medium">Autonomous Logistics</span> — LN2 and -80°C smart inventory fully digitized, ensuring resource optimization and sample integrity.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-cyan-400 flex-shrink-0" />
                <span><span className="text-slate-300 font-medium">Institutional Intelligence</span> — Failure dashboard transforms experimental setbacks into searchable data for de-risked, high-impact research.</span>
              </div>
              <div className="col-span-2 flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-indigo-400 flex-shrink-0" />
                <span><span className="text-slate-300 font-medium">Synthesized Output</span> — Integrated data analysis and figure maker for lossless bench-to-manuscript transition, preserving data integrity at every step.</span>
              </div>
            </div>
            <p className="text-[13px] text-slate-500 mt-4 italic">
              P.R.I.S.M. is the infrastructure that makes biological breakthroughs a scalable and reproducible reality.
            </p>
          </div>
          <div className="mt-4 pt-3 border-t border-white/[0.06] w-full">
            <p className="text-xs text-slate-500">Designed, built & owned by Chaturvedi Lab.</p>
            <p className="text-[11px] text-slate-600 mt-1">&copy; 2026 Chaturvedi Lab. All rights reserved.</p>
          </div>
        </div>

        {/* Right side - Auth card */}
        <div className="w-full max-w-[420px]">
          {/* Mobile branding (shown only on small screens) */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" 
                style={{ fontFamily: '"Space Grotesk", sans-serif', letterSpacing: '-0.03em' }}>
              P.R.I.S.M.
            </h1>
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-2">Programmable Research Integration & Systems Management</p>
            <p className="text-sm font-semibold"
               style={{ 
                 background: 'linear-gradient(135deg, rgb(139,92,246) 0%, rgb(6,182,212) 50%, rgb(59,130,246) 100%)',
                 WebkitBackgroundClip: 'text',
                 WebkitTextFillColor: 'transparent'
               }}>
              A Unified Framework for Biological Orchestration
            </p>
          </div>

          <div className="auth-card" data-testid="auth-card">
            {/* Logo */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1, #06B6D4)' }}>
                <Flask size={22} weight="bold" className="text-white" />
              </div>
              <div>
                <span className="heading-section block">Chaturvedi Lab</span>
              </div>
            </div>

            <h1 className="heading-subsection text-center mb-6" data-testid="auth-title">
              {isLogin ? 'Sign in to your account' : 'Create your account'}
            </h1>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="auth-error">
                <WarningCircle size={16} style={{ color: '#F87171' }} />
                <span className="text-sm" style={{ color: '#FCA5A5' }}>{error}</span>
              </div>
            )}

            {pendingApproval && (
              <div className="rounded-lg p-4 mb-4 text-center" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }} data-testid="pending-approval-msg">
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.12)' }}>
                  <Check size={24} style={{ color: '#818CF8' }} />
                </div>
                <h3 className="text-sm font-semibold mb-1" style={{ color: '#F1F5F9' }}>Account Created!</h3>
                <p className="text-xs leading-relaxed" style={{ color: '#8896A8' }}>
                  Your request has been sent to the lab PI for approval.<br />
                  You'll receive an email once your account is approved.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                  <div className="relative">
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Dr. Jane Smith"
                      value={formData.name}
                      onChange={handleChange}
                      className="pr-10 input-field w-full"
                      required={!isLogin}
                      data-testid="register-name-input"
                    />
                    <User size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <div className="relative">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pr-10 input-field w-full"
                    required
                    data-testid="email-input"
                  />
                  <Envelope size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    className="pr-10 input-field w-full"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <Lock size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-sm font-medium">Role</Label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="input-field w-full"
                    data-testid="role-select"
                  >
                    <option value="student">Student</option>
                    <option value="researcher">Researcher</option>
                    <option value="pi">Principal Investigator</option>
                  </select>
                </div>
              )}

              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="spinner w-4 h-4" />
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </span>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>

              {isLogin && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setShowForgot(true); setError(''); setForgotSent(false); setForgotEmail(''); }}
                    className="text-xs text-slate-400 hover:underline transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}
            </form>

            {/* Forgot Password Form */}
            {showForgot && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="forgot-password-modal">
                <div className="rounded-xl shadow-lg shadow-black/30 p-6 max-w-sm w-full mx-4" style={{ background: 'rgba(13,20,36,0.95)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <h3 className="text-lg font-semibold mb-1" style={{ color: '#F1F5F9' }}>Reset Password</h3>
                  {forgotSent ? (
                    <div className="text-center py-4" data-testid="forgot-sent-msg">
                      <Check size={40} className="mx-auto mb-3" style={{ color: '#818CF8' }} />
                      <p className="text-sm mb-1" style={{ color: '#E2E8F0' }}>Check your email!</p>
                      <p className="text-xs leading-relaxed" style={{ color: '#8896A8' }}>
                        If an account exists for <strong style={{ color: '#CBD5E1' }}>{forgotEmail}</strong>, we've sent a reset link. Check your inbox (and spam).
                      </p>
                      <Button className="btn-primary mt-4" onClick={() => setShowForgot(false)} data-testid="forgot-done-btn">
                        Back to Login
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword}>
                      <p className="text-xs mb-3" style={{ color: '#8896A8' }}>
                        Enter your email and we'll send you a link to reset your password.
                      </p>
                      {error && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                          <WarningCircle size={14} style={{ color: '#F87171' }} />
                          <span className="text-xs" style={{ color: '#FCA5A5' }}>{error}</span>
                        </div>
                      )}
                      <div className="relative mb-3">
                        <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          value={forgotEmail}
                          onChange={e => setForgotEmail(e.target.value)}
                          className="input-field pl-10"
                          required
                          data-testid="forgot-email-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" className="flex-1 btn-secondary" onClick={() => { setShowForgot(false); setError(''); }} data-testid="forgot-cancel-btn">
                          Cancel
                        </Button>
                        <Button type="submit" className="flex-1 btn-primary" disabled={loading} data-testid="forgot-submit-btn">
                          {loading ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="text-sm hover:underline transition-colors" style={{ color: '#818CF8' }}
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
